// Sidebar navigation — extracted verbatim from App.js so the nav can be
// unit-tested in isolation (App.js drags the entire route tree with it).
// Structure, class names and behaviour are unchanged; the only functional
// addition is the Management > Wallet Management leaf (/wallet/pending),
// which previously had NO entry point anywhere in the UI (owner).
import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faShuttleVan, faHome, faUsers, faBuilding, faCalendarAlt, faUserFriends,
  faCog, faSoap, faWarehouse, faFileInvoice, faAngleLeft, faAngleRight,
  faChartSimple, faWallet, faChevronDown, faBoxesStacked, faSliders, faSackDollar,
  faBullhorn,
  faClipboardList, faList, faUser, faInbox, faBoxOpen, faScrewdriverWrench, faFileSignature, faMapLocationDot,
} from '@fortawesome/free-solid-svg-icons';
import { useNotification } from './contexts/NotificationContext';

// Helper: does the current path match this route?
export function isPathActive(currentPath, target, exact = false) {
  if (exact) return currentPath === target;
  return currentPath === target || currentPath.startsWith(target + '/');
}

// Single nav link (leaf)
function NavLeaf({ to, icon, label, isCollapsed, onClick, badge, currentPath, exact = false }) {
  const active = isPathActive(currentPath, to, exact);
  return (
    <li className="nav-item">
      <Link
        to={to}
        onClick={onClick}
        title={label}
        className={active ? 'is-active' : ''}
      >
        <div className="icon-container">
          <FontAwesomeIcon icon={icon} />
        </div>
        <span className="sidebar-text">{label}</span>
        {!isCollapsed && badge > 0 && (
          <span className="sidebar-notification-badge">{badge}</span>
        )}
      </Link>
    </li>
  );
}

// Collapsible nav group (parent with children) — accordion behavior via parent state
// If `to` is provided, clicking the header navigates there AND opens the subgroup
function NavGroup({ label, icon, to, children, childPaths, isCollapsed, currentPath, onLinkClick, openGroup, setOpenGroup }) {
  const hasActiveChild = childPaths?.some(p => isPathActive(currentPath, p));
  const isActive = to ? isPathActive(currentPath, to) : false;
  const isOpen = openGroup === label;

  // Auto-open if the current route matches this group
  useEffect(() => {
    if (hasActiveChild || isActive) setOpenGroup(label);
  }, [hasActiveChild, isActive, label, setOpenGroup]);

  // In collapsed mode, don't show the group header — just show children as individual items
  if (isCollapsed) {
    return <>{children}</>;
  }

  const handleClick = () => {
    setOpenGroup(isOpen ? null : label);
    if (onLinkClick) onLinkClick();
  };

  return (
    <li className="nav-group">
      {to ? (
        <Link
          to={to}
          onClick={handleClick}
          className={`nav-group__header ${hasActiveChild || isActive ? 'has-active' : ''}`}
        >
          <div className="icon-container">
            <FontAwesomeIcon icon={icon} />
          </div>
          <span className="sidebar-text">{label}</span>
          <FontAwesomeIcon icon={faChevronDown} className={`nav-group__caret ${isOpen ? 'is-open' : ''}`} />
        </Link>
      ) : (
        <button
          type="button"
          className={`nav-group__header ${hasActiveChild ? 'has-active' : ''}`}
          onClick={handleClick}
        >
          <div className="icon-container">
            <FontAwesomeIcon icon={icon} />
          </div>
          <span className="sidebar-text">{label}</span>
          <FontAwesomeIcon icon={faChevronDown} className={`nav-group__caret ${isOpen ? 'is-open' : ''}`} />
        </button>
      )}
      <ul className={`nav-group__children ${isOpen ? 'is-open' : ''}`}>
        {children}
      </ul>
    </li>
  );
}

// Sidebar with navigation links
export default function Sidebar({ isOpen, toggleSidebar, isCollapsed, toggleCollapsed }) {
  const userRole = localStorage.getItem('userRole');
  const { unreadCount, pendingRequestCount } = useNotification();
  const location = useLocation();
  const currentPath = location.pathname;

  // Accordion: only one nav group can be open at a time
  const [openGroup, setOpenGroup] = useState(null);

  const handleNavClick = () => {
    if (isOpen) toggleSidebar();
  };

  const isStaff = userRole === 'admin' || userRole === 'manager' || userRole === 'supervisor';
  const canSeeWarehouse = isStaff || userRole === 'warehouse';

  return (
    <div className={`sidebar ${isOpen ? 'open' : ''} ${isCollapsed ? 'collapsed' : ''}`}>
      <div className="sidebar-header">
        <Link
          to={userRole === 'client' ? "/client/home" : "/"}
          onClick={handleNavClick}
          className="logo"
          title="Home"
        >
          <img src="/logo.png" alt="Logo" />
        </Link>
      </div>

      <nav>
        <ul>
          {isStaff && (
            <>
              <NavLeaf to="/" exact icon={faHome} label="Home" isCollapsed={isCollapsed} onClick={handleNavClick} badge={unreadCount} currentPath={currentPath} />

              {/* A plain leaf since the Wallet child folded into the page
                  itself (2026-09-02) — a group with no children only rendered
                  a chevron pointing at nothing. */}
              <NavLeaf to="/clients" icon={faUsers} label="Clients" isCollapsed={isCollapsed} onClick={handleNavClick} currentPath={currentPath} />

              <NavLeaf to="/properties" icon={faBuilding} label="Properties" isCollapsed={isCollapsed} onClick={handleNavClick} currentPath={currentPath} />
              <NavLeaf to="/bookings" icon={faCalendarAlt} label="Bookings" isCollapsed={isCollapsed} onClick={handleNavClick} currentPath={currentPath} />
              <NavLeaf to="/requests" icon={faInbox} label="Requests" isCollapsed={isCollapsed} onClick={handleNavClick} badge={pendingRequestCount} currentPath={currentPath} />
              <NavLeaf to="/team" icon={faUserFriends} label="Team" isCollapsed={isCollapsed} onClick={handleNavClick} currentPath={currentPath} />
            </>
          )}

          {canSeeWarehouse && (
            <NavGroup
              label="Warehouse"
              icon={faBoxesStacked}
              isCollapsed={isCollapsed}
              currentPath={currentPath}
              childPaths={['/warehouse', '/laundry']}
              openGroup={openGroup}
              setOpenGroup={setOpenGroup}
            >
              {/* `exact`: /warehouse/stock is a sibling page, not this one — without
                  it the duty report would light up on the stock screen too. */}
              <NavLeaf to="/warehouse" exact icon={faWarehouse} label="Warehouse" isCollapsed={isCollapsed} onClick={handleNavClick} currentPath={currentPath} />
              <NavLeaf to="/warehouse/stock" icon={faBoxOpen} label="Stock" isCollapsed={isCollapsed} onClick={handleNavClick} currentPath={currentPath} />
              <NavLeaf to="/laundry" icon={faSoap} label="Laundry" isCollapsed={isCollapsed} onClick={handleNavClick} currentPath={currentPath} />
            </NavGroup>
          )}

          {(isStaff || userRole === 'driver') && (
            <NavLeaf to="/drivers" icon={faShuttleVan} label="Drivers" isCollapsed={isCollapsed} onClick={handleNavClick} currentPath={currentPath} />
          )}

          {isStaff && (
            <>
              {/* Leads sits directly above Statistics: it is the other place in
                  the sidebar you go to read numbers rather than do work
                  (PLAN_leads.md). */}
              <NavLeaf to="/leads" icon={faBullhorn} label="Leads" isCollapsed={isCollapsed} onClick={handleNavClick} currentPath={currentPath} />
              <NavLeaf to="/statistics" icon={faChartSimple} label="Statistics" isCollapsed={isCollapsed} onClick={handleNavClick} currentPath={currentPath} />
            </>
          )}

          {/* Tools (owner, 2026-09-21): office utilities — the payment warning
              letters and the coverage checker. Admins, managers, supervisors. */}
          {isStaff && (
            <NavGroup
              label="Tools"
              icon={faScrewdriverWrench}
              isCollapsed={isCollapsed}
              currentPath={currentPath}
              childPaths={['/tools']}
              openGroup={openGroup}
              setOpenGroup={setOpenGroup}
            >
              <NavLeaf to="/tools/warning-letters" icon={faFileSignature} label="Warning Letters" isCollapsed={isCollapsed} onClick={handleNavClick} currentPath={currentPath} />
              <NavLeaf to="/tools/coverage" icon={faMapLocationDot} label="Coverage Check" isCollapsed={isCollapsed} onClick={handleNavClick} currentPath={currentPath} />
            </NavGroup>
          )}

          {userRole === 'manager' && (
            <NavGroup
              label="Management"
              icon={faSliders}
              isCollapsed={isCollapsed}
              currentPath={currentPath}
              childPaths={['/audit-logs', '/items', '/invoices', '/payments']}
              openGroup={openGroup}
              setOpenGroup={setOpenGroup}
            >
              <NavLeaf to="/audit-logs" icon={faClipboardList} label="Audit Logs" isCollapsed={isCollapsed} onClick={handleNavClick} currentPath={currentPath} />
              <NavLeaf to="/items" icon={faList} label="Items" isCollapsed={isCollapsed} onClick={handleNavClick} currentPath={currentPath} />
              <NavLeaf to="/invoices" icon={faFileInvoice} label="Invoices" isCollapsed={isCollapsed} onClick={handleNavClick} currentPath={currentPath} />
              <NavLeaf to="/payments" icon={faWallet} label="Payments" isCollapsed={isCollapsed} onClick={handleNavClick} currentPath={currentPath} />
              <NavLeaf to="/salaries" icon={faSackDollar} label="Salaries" isCollapsed={isCollapsed} onClick={handleNavClick} currentPath={currentPath} />
            </NavGroup>
          )}

          {(isStaff || userRole === 'warehouse' || userRole === 'driver' || userRole === 'cleaner') && (
            <NavLeaf to="/settings" icon={faCog} label="Settings" isCollapsed={isCollapsed} onClick={handleNavClick} currentPath={currentPath} />
          )}

          {userRole === 'client' && (
            <>
              <NavLeaf to="/client/home" icon={faHome} label="Home" isCollapsed={isCollapsed} onClick={handleNavClick} currentPath={currentPath} />
              <NavLeaf to="/client/bookings" icon={faCalendarAlt} label="Bookings" isCollapsed={isCollapsed} onClick={handleNavClick} currentPath={currentPath} />
              <NavLeaf to="/client/properties" icon={faBuilding} label="Properties" isCollapsed={isCollapsed} onClick={handleNavClick} currentPath={currentPath} />
              <NavLeaf to="/client/tasks" icon={faClipboardList} label="Tasks" isCollapsed={isCollapsed} onClick={handleNavClick} currentPath={currentPath} />
              <NavLeaf to="/client/reports" icon={faChartSimple} label="Reports" isCollapsed={isCollapsed} onClick={handleNavClick} currentPath={currentPath} />
              <NavLeaf to="/client/invoices" icon={faFileInvoice} label="Invoices" isCollapsed={isCollapsed} onClick={handleNavClick} currentPath={currentPath} />
              <NavLeaf to="/client/wallet" icon={faWallet} label="Wallet" isCollapsed={isCollapsed} onClick={handleNavClick} currentPath={currentPath} />
              <NavLeaf to="/client/profile" icon={faUser} label="Account" isCollapsed={isCollapsed} onClick={handleNavClick} currentPath={currentPath} />
              <NavLeaf to="/client/settings" icon={faCog} label="Settings" isCollapsed={isCollapsed} onClick={handleNavClick} currentPath={currentPath} />
            </>
          )}
        </ul>
      </nav>
      <div className='sidebar-footer'>
        <button
          type="button"
          className="sidebar-collapse-button"
          onClick={toggleCollapsed}
          title={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
        >
          <FontAwesomeIcon icon={isCollapsed ? faAngleRight : faAngleLeft} />
        </button>
      </div>
    </div>
  );
}
